import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #f8f9fa;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  background: #f8f9fa;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: white;
    color: #333;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  background: #f8f9fa;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  padding: 16px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const StatusIndicator = styled.div`
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  background: ${props => props.disabled ? '#f8d7da' : '#d4edda'};
  color: ${props => props.disabled ? '#721c24' : '#155724'};
  font-weight: 500;
  text-align: center;
`;

const DepositForm = ({ disabled = false }) => {
  const [formData, setFormData] = useState({
    commodityType: '',
    grade: '',
    quantity: '',
    unit: '',
    supplierName: '',
    transportId: '',
    originLocation: '',
    destinationWarehouse: '',
    certificateNumbers: '',
    notes: ''
  });

  const [submitting, setSubmitting] = useState(false);

  const commodityTypes = [
    { value: 'Sugar', label: 'Sugar', units: ['tons', 'kg', 'lbs'] },
    { value: 'Coffee', label: 'Coffee', units: ['bags', 'tons', 'kg'] },
    { value: 'Gold', label: 'Gold', units: ['oz', 'kg', 'grams'] },
    { value: 'Silver', label: 'Silver', units: ['oz', 'kg', 'grams'] }
  ];

  const warehouses = [
    'Miami Warehouse - Zone A',
    'New York Storage Facility',
    'Chicago Distribution Center',
    'Los Angeles Port Terminal'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;

    setSubmitting(true);

    try {
      // Validate required fields
      if (!formData.commodityType || !formData.quantity || !formData.supplierName) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Simulate deposit processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      toast.success(`Deposit request submitted for ${formData.quantity} ${formData.unit} of ${formData.commodityType}`);
      
      // Reset form
      setFormData({
        commodityType: '',
        grade: '',
        quantity: '',
        unit: '',
        supplierName: '',
        transportId: '',
        originLocation: '',
        destinationWarehouse: '',
        certificateNumbers: '',
        notes: ''
      });

    } catch (error) {
      toast.error('Failed to process deposit request');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCommodity = commodityTypes.find(c => c.value === formData.commodityType);
  const availableUnits = selectedCommodity?.units || [];

  return (
    <FormContainer>
      <StatusIndicator disabled={disabled}>
        {disabled 
          ? '🛑 Deposit operations are currently suspended'
          : '✅ Deposit operations are active'
        }
      </StatusIndicator>

      <form onSubmit={handleSubmit}>
        <FormContainer>
          {/* Basic Commodity Information */}
          <FormRow>
            <FormGroup>
              <Label>Commodity Type *</Label>
              <Select
                name="commodityType"
                value={formData.commodityType}
                onChange={handleInputChange}
                disabled={disabled}
                required
              >
                <option value="">Select commodity...</option>
                {commodityTypes.map(commodity => (
                  <option key={commodity.value} value={commodity.value}>
                    {commodity.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Grade/Quality</Label>
              <Input
                type="text"
                name="grade"
                value={formData.grade}
                onChange={handleInputChange}
                placeholder="e.g., Premium, Grade A"
                disabled={disabled}
              />
            </FormGroup>
          </FormRow>

          {/* Quantity Information */}
          <FormRow>
            <FormGroup>
              <Label>Quantity *</Label>
              <Input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                placeholder="Enter quantity"
                min="0"
                step="0.01"
                disabled={disabled}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Unit</Label>
              <Select
                name="unit"
                value={formData.unit}
                onChange={handleInputChange}
                disabled={disabled || !formData.commodityType}
              >
                <option value="">Select unit...</option>
                {availableUnits.map(unit => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>

          {/* Supplier and Transport Information */}
          <FormRow>
            <FormGroup>
              <Label>Supplier Name *</Label>
              <Input
                type="text"
                name="supplierName"
                value={formData.supplierName}
                onChange={handleInputChange}
                placeholder="Enter supplier company name"
                disabled={disabled}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Transport ID</Label>
              <Input
                type="text"
                name="transportId"
                value={formData.transportId}
                onChange={handleInputChange}
                placeholder="e.g., Truck #123, Ship VESSEL001"
                disabled={disabled}
              />
            </FormGroup>
          </FormRow>

          {/* Location Information */}
          <FormRow>
            <FormGroup>
              <Label>Origin Location</Label>
              <Input
                type="text"
                name="originLocation"
                value={formData.originLocation}
                onChange={handleInputChange}
                placeholder="Origin city/country"
                disabled={disabled}
              />
            </FormGroup>

            <FormGroup>
              <Label>Destination Warehouse</Label>
              <Select
                name="destinationWarehouse"
                value={formData.destinationWarehouse}
                onChange={handleInputChange}
                disabled={disabled}
              >
                <option value="">Select warehouse...</option>
                {warehouses.map(warehouse => (
                  <option key={warehouse} value={warehouse}>
                    {warehouse}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>

          {/* Additional Information */}
          <FormGroup>
            <Label>Quality Certificate Numbers</Label>
            <Input
              type="text"
              name="certificateNumbers"
              value={formData.certificateNumbers}
              onChange={handleInputChange}
              placeholder="e.g., QC-001, ISO-002, ORG-003"
              disabled={disabled}
            />
          </FormGroup>

          <FormGroup>
            <Label>Additional Notes</Label>
            <TextArea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Any additional information about this deposit..."
              disabled={disabled}
            />
          </FormGroup>

          <SubmitButton 
            type="submit" 
            disabled={disabled || submitting}
          >
            {submitting ? 'Processing Deposit...' : 'Submit Deposit Request'}
          </SubmitButton>
        </FormContainer>
      </form>
    </FormContainer>
  );
};

export default DepositForm; 